import type React from 'react'
import { ROTATE_CURSOR } from '@/components/app/editor/selection-chrome/selection-chrome-constants'
import { CROP_ACCENT_COLOR } from '../constants/crop-chrome'
import type { ShapeRotateDotLayout } from '../helpers/badge-layout'

interface CropShapeRotateDotProps {
  layout: ShapeRotateDotLayout
  /** Dot radius (canvas units). */
  handleHalf: number
  /** Stroke width (canvas units). */
  sw: number
  onShapeRotateDown: (e: React.PointerEvent) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: (e: React.PointerEvent) => void
}

/**
 * Shape rotate handle — rotates the shape's own path/frame (`ShapeData.rotation`), independent
 * of the photo-rotate dial (which only spins the photo content via `photoFill.rotation`). This is
 * the "outer select area" rotate handle normal (non-crop) selection chrome shows above every
 * shape — previously hidden entirely in crop mode once its own chrome got suppressed to avoid a
 * duplicate selection box (see the crop mode memory notes on that fix); restored here,
 * purpose-built for crop mode instead of reusing ShapeManager's chrome-rotate gesture, since that
 * gesture targets the current selection and crop mode deliberately clears selection to keep the
 * two frames from visually overlapping.
 */
export function CropShapeRotateDot({
  layout,
  handleHalf,
  sw,
  onShapeRotateDown,
  onPointerMove,
  onPointerUp,
}: CropShapeRotateDotProps) {
  return (
    <>
      <line
        data-testid="crop-shape-rotate-line"
        x1={layout.stemStartX}
        y1={layout.stemStartY}
        x2={layout.dotX}
        y2={layout.dotY}
        stroke={CROP_ACCENT_COLOR}
        strokeWidth={sw}
        vectorEffect="non-scaling-stroke"
        pointerEvents="none"
      />
      <circle
        data-testid="crop-shape-rotate-handle"
        cx={layout.dotX}
        cy={layout.dotY}
        r={handleHalf}
        fill="white"
        stroke={CROP_ACCENT_COLOR}
        strokeWidth={sw}
        vectorEffect="non-scaling-stroke"
        style={{ pointerEvents: 'all', cursor: ROTATE_CURSOR }}
        onPointerDown={onShapeRotateDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
    </>
  )
}
